/**
 * Where a pharmacy's booking page lives.
 *
 * Two shapes, one slug:
 *
 * - `two-hills.myonerx.ca` — the public address, and what goes on a poster.
 * - `book.myonerx.ca/two-hills` — the same page by path.
 *
 * The path form is not a legacy fallback; it is the only one that works on
 * `localhost`, where wildcard subdomains need a hosts-file entry that no
 * developer should have to add to run the app. Both resolve to the same tenant
 * because the slug is globally unique, and it is stored as a DNS label
 * (lowercase, 3–63 characters, no leading or trailing hyphen) precisely so the
 * subdomain form is possible without a second identifier.
 */

/**
 * Hostnames under the booking domain that are **not** a pharmacy.
 *
 * Deliberately small, and a different question from the server's reserved list.
 * This one answers "does this hostname address a pharmacy?" — it needs only the
 * labels that legitimately serve something else. The server's list answers "may
 * a pharmacy claim this?", which is a much longer list including every
 * infrastructure name, and it is enforced where it matters: at the write.
 */
const NON_TENANT_HOSTS: ReadonlySet<string> = new Set([
  'www',
  'book',
  'booking',
  'app',
  'api',
  'admin',
]);

/**
 * The pharmacy a hostname addresses, or null when it addresses none.
 *
 * Null for the apex, for `www`, for anything that is not under the booking
 * domain, and for a deeper name like `a.b.myonerx.ca` — a pharmacy is exactly
 * one label, so anything else is somebody else's host and must not be treated
 * as a tenant.
 */
export function slugFromHost(
  hostname: string,
  baseDomain: string,
): string | null {
  const host = hostname.trim().toLowerCase().replace(/\.$/, '');
  const base = baseDomain.trim().toLowerCase().replace(/^\.|\.$/g, '');
  if (!host || !base) return null;

  // The apex itself is the marketing site, not a pharmacy.
  if (host === base) return null;

  const suffix = `.${base}`;
  if (!host.endsWith(suffix)) return null;

  const label = host.slice(0, -suffix.length);
  // Exactly one label. `a.b.myonerx.ca` is not a pharmacy.
  if (!label || label.includes('.')) return null;
  if (NON_TENANT_HOSTS.has(label)) return null;
  // Must look like the slug the database would have accepted, or a malformed
  // host would send a lookup for a tenant that could never exist.
  if (!isSlugShaped(label)) return null;

  return label;
}

/** The DNS-label rule the `appt_booking_profile` CHECK constraint enforces. */
export function isSlugShaped(value: string): boolean {
  return (
    value.length >= 3 &&
    value.length <= 63 &&
    /^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/.test(value)
  );
}

/**
 * The address to show a pharmacist and print on a poster.
 *
 * Always the subdomain form: it is shorter, it reads aloud without the word
 * "slash", and it is the one that looks like the pharmacy's own.
 */
export function bookingUrlFor(slug: string, baseDomain: string): string {
  return `https://${slug}.${baseDomain.replace(/^\./, '')}`;
}
